<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯服務項目' : '新增服務項目' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="goBack">
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading || saving" @click="save">
          {{ saving ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <form v-else class="form-container service-form" @submit.prevent="save">
        <div class="form-section">
          <h2 class="section-title">基本資料</h2>
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required-label" for="service-code">服務代碼</label>
              <input
                id="service-code"
                v-model.trim="form.serviceCode"
                type="text"
                class="form-control"
                maxlength="50"
                :disabled="isEdit"
                required
              />
              <small v-if="isEdit" class="form-text">服務代碼建立後無法修改</small>
            </div>

            <div class="form-group">
              <label class="form-label required-label" for="service-name">服務名稱</label>
              <input
                id="service-name"
                v-model.trim="form.name"
                type="text"
                class="form-control"
                maxlength="100"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="category-name">分類</label>
              <input
                id="category-name"
                v-model.trim="form.categoryName"
                type="text"
                class="form-control"
                maxlength="100"
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="service-price">價格</label>
              <input
                id="service-price"
                v-model.number="form.price"
                type="number"
                class="form-control"
                min="0"
                step="1"
                placeholder="未填寫"
              />
              <span class="form-hint">未填寫時，顧客確認預約不會顯示金額。</span>
            </div>

            <div class="form-group">
              <label class="form-label required-label" for="duration-minutes">預計分鐘</label>
              <input
                id="duration-minutes"
                v-model.number="form.durationMinutes"
                type="number"
                class="form-control"
                min="5"
                max="1440"
                step="1"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="sort-order">排序</label>
              <input
                id="sort-order"
                v-model.number="form.sortOrder"
                type="number"
                class="form-control"
                step="1"
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="service-status">狀態</label>
              <select id="service-status" v-model="form.status" class="form-control">
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h2 class="section-title">服務說明</h2>
          <div class="form-group">
            <label class="form-label" for="service-description">說明</label>
            <textarea
              id="service-description"
              v-model.trim="form.description"
              class="form-control"
              rows="5"
              placeholder="輸入服務內容或注意事項"
            ></textarea>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { showError, showSuccess } from '@/utils/swal.js';

const emptyForm = () => ({
  serviceCode: '',
  categoryName: '',
  name: '',
  description: '',
  price: null,
  durationMinutes: 60,
  status: 'active',
  sortOrder: 0
});

export default {
  name: 'SalonServiceForm',
  data() {
    return {
      loading: false,
      saving: false,
      form: emptyForm()
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    }
  },
  async mounted() {
    if (this.isEdit) {
      await this.loadService();
    }
  },
  methods: {
    async loadService() {
      this.loading = true;
      try {
        const response = await salonService.getService(this.$route.params.id);
        this.form = {
          ...emptyForm(),
          ...response.data,
          categoryName: response.data.categoryName || '',
          description: response.data.description || '',
          price: response.data.price == null || response.data.price === ''
            ? null
            : Number(response.data.price),
          durationMinutes: Number(response.data.durationMinutes || 60),
          sortOrder: Number(response.data.sortOrder || 0)
        };
      } catch (error) {
        await this.handleError(error, '服務項目載入失敗');
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.saving) return;

      this.saving = true;
      try {
        const body = {
          ...this.form,
          categoryName: this.form.categoryName || null,
          description: this.form.description || null,
          price: this.form.price == null || this.form.price === ''
            ? null
            : Number(this.form.price),
          durationMinutes: Number(this.form.durationMinutes),
          sortOrder: Number(this.form.sortOrder)
        };
        delete body.id;
        delete body.clientId;
        delete body.platformId;
        delete body.createdAt;
        delete body.updatedAt;

        if (this.isEdit) {
          delete body.serviceCode;
          await salonService.updateService(this.$route.params.id, body);
        } else {
          await salonService.createService(body);
        }

        await showSuccess(
          this.isEdit ? '更新成功' : '建立成功',
          '服務項目已儲存'
        );
        this.goBack();
      } catch (error) {
        await this.handleError(error, '服務項目儲存失敗');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'SalonServices' });
    },
    async handleError(error, fallback) {
      const message = error.response?.data?.message || error.message || fallback;
      await showError(fallback, message);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';

.service-form {
  max-width: 960px;
  margin: 0 auto;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.form-text {
  color: #64748b;
  display: block;
  margin-top: 6px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
